import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, message } from 'antd';
import { AudioOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useI18n } from '../../hooks/useI18n';
import './VoiceInput.scss';

const VoiceInput = ({ 
	visible, 
	onClose, 
	onApply, 
	parseFunction,
	instructionsData = {}
}) => {
	const { t } = useI18n();
	const [isListening, setIsListening] = useState(false);
	const [transcript, setTranscript] = useState('');
	const [interimTranscript, setInterimTranscript] = useState('');
	const recognitionRef = useRef(null);

	// Initialize speech recognition
	useEffect(() => {
		if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
			const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
			recognitionRef.current = new SpeechRecognition();
			recognitionRef.current.continuous = true;
			recognitionRef.current.interimResults = true;
			recognitionRef.current.lang = 'en-US';

			recognitionRef.current.onresult = (event) => {
				let interimText = '';
				let finalText = '';

				for (let i = event.resultIndex; i < event.results.length; i++) {
					const transcript = event.results[i][0].transcript;
					if (event.results[i].isFinal) {
						finalText += transcript + ' ';
					} else {
						interimText += transcript;
					}
				}

				if (finalText) {
					setTranscript((prev) => prev + finalText);
				}
				setInterimTranscript(interimText);
			};

			recognitionRef.current.onerror = (event) => {
				console.error('Speech recognition error:', event.error);
				if (event.error === 'no-speech') {
					message.warning(t('voice_input.no_speech_detected'));
				} else if (event.error === 'not-allowed') {
					message.error(t('voice_input.microphone_denied'));
				} else {
					message.error(t('voice_input.recognition_error'));
				}
				setIsListening(false);
			};

			recognitionRef.current.onend = () => {
				setIsListening(false);
			};
		}

		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, [t]);

	// Auto-start listening when modal opens
	useEffect(() => {
		if (visible && recognitionRef.current) {
			startListening();
		} else if (!visible) {
			stopListening();
			setTranscript('');
			setInterimTranscript('');
		}
	}, [visible]);

	const startListening = () => {
		if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
			message.error(t('voice_input.not_supported'));
			return;
		}

		setTranscript('');
		setInterimTranscript('');
		setIsListening(true);
		
		try {
			recognitionRef.current.start();
			message.info(t('voice_input.listening_started'));
		} catch (error) {
			console.error('Error starting recognition:', error);
			setIsListening(false);
		}
	};

	const stopListening = () => {
		if (recognitionRef.current) {
			recognitionRef.current.stop();
		}
		setIsListening(false);
	};

	const handleApply = () => {
		const fullTranscript = transcript.trim().toLowerCase();
		
		if (!fullTranscript) {
			message.warning(t('voice_input.no_speech_detected'));
			return;
		}

		// Use the provided parse function
		const extractedData = parseFunction ? parseFunction(fullTranscript) : {};
		
		if (Object.keys(extractedData).length === 0) {
			message.warning(t('voice_input.no_data_extracted'));
			return;
		}

		onApply(extractedData);
		message.success(t('voice_input.applied_successfully'));
		handleCancel();
	};

	const handleCancel = () => {
		stopListening();
		setTranscript('');
		setInterimTranscript('');
		onClose();
	};

	return (
		<Modal
			title={
				<div style={{ textAlign: 'center' }}>
					<AudioOutlined style={{ fontSize: '24px', color: isListening ? '#ff4d4f' : '#1890ff' }} />
					<div style={{ marginTop: '8px' }}>{t('voice_input.title')}</div>
				</div>
			}
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={600}
			centered
			bodyStyle={{
				padding: '24px',
				minHeight: '300px',
			}}
			className="voice-input-modal">
			<div style={{ textAlign: 'center' }}>
				<div
					className={`voice-status-box ${isListening ? 'listening' : ''}`}>
					<div className="status-title">
						{isListening ? t('voice_input.listening') : t('voice_input.ready')}
					</div>
					<div className="status-description">
						{instructionsData.example || t('voice_input.default_example')}
					</div>
					{instructionsData.tip && (
						<div className="status-tip">
							{instructionsData.tip}
						</div>
					)}
				</div>

				{/* Transcript Display */}
				<div className="transcript-box">
					<div className="transcript-label">
						{t('voice_input.transcript')}:
					</div>
					<div className="transcript-content">
						{transcript || interimTranscript ? (
							<>
								<span>{transcript}</span>
								<span className="interim-text">{interimTranscript}</span>
							</>
						) : (
							<span className="placeholder-text">{t('voice_input.transcript_placeholder')}</span>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				<Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
					{isListening ? (
						<Button
							danger
							size="large"
							icon={<StopOutlined />}
							onClick={stopListening}
							className="action-button">
							{t('voice_input.stop_listening')}
						</Button>
					) : (
						<Button
							type="primary"
							size="large"
							icon={<AudioOutlined />}
							onClick={startListening}
							className="action-button">
							{t('voice_input.start_listening')}
						</Button>
					)}
					<Button
						type="primary"
						size="large"
						icon={<CheckCircleOutlined />}
						onClick={handleApply}
						disabled={!transcript}
						className="action-button apply-button">
						{t('common.submit')}
					</Button>
					<Button 
						size="large" 
						onClick={handleCancel}
						className="action-button">
						{t('common.cancel')}
					</Button>
				</Space>

				{/* Instructions */}
				<div className="instructions-box">
					<div className="instructions-title">
						💡 {t('voice_input.tips_title')}
					</div>
					<ul className="instructions-list">
						{instructionsData.tips ? (
							instructionsData.tips.map((tip, index) => (
								<li key={index}>{tip}</li>
							))
						) : (
							<>
								<li>{t('voice_input.tip_1')}</li>
								<li>{t('voice_input.tip_2')}</li>
								<li>{t('voice_input.tip_3')}</li>
							</>
						)}
					</ul>
				</div>
			</div>
		</Modal>
	);
};

export default VoiceInput;
